import React, { ChangeEvent, FormEvent, useCallback, useMemo, useState } from 'react';
import { useDifyCapability } from '../../hooks/useDifyCapability';
import { 
  AiDifyCapability, 
  AiDifyCapabilityCreatePayload, 
  AiDifyCapabilityFeatures,
  AiDifyCapabilityType,
  DEFAULT_CAPABILITY_FEATURES,
  InputFieldDefinition,
  InputFieldType,
  DEFAULT_INPUT_FIELDS,
  DEFAULT_WELCOME_CONFIG,
  WelcomeConfig,
  WelcomePromptCard,
} from '../../types/aiDifyCapability';
import './styles.css';

type FormMode = 'create' | 'edit';

/** 特性开关配置项，用于统一渲染 */
interface FeatureToggleItem {
  readonly key: keyof AiDifyCapabilityFeatures;
  readonly label: string;
  readonly description: string;
}

const FEATURE_TOGGLE_LIST: ReadonlyArray<FeatureToggleItem> = [
  { key: 'simple_mode', label: '快速模式', description: '精简交互流程，适合轻量场景' },
  { key: 'enhanced_mode', label: '增强模式', description: '启用深度推理与多步分析' },
  { key: 'site_search', label: '站内搜索', description: '允许搜索站内知识库内容' },
  { key: 'skills', label: '技能/Skills', description: '挂载外部技能插件扩展能力' },
];

/** 输入字段类型选项 */
const INPUT_FIELD_TYPE_OPTIONS: ReadonlyArray<{ value: InputFieldType; label: string }> = [
  { value: 'text', label: '文本' },
  { value: 'number', label: '数字' },
  { value: 'textarea', label: '多行文本' },
  { value: 'select', label: '下拉选择' },
  { value: 'hidden', label: '隐藏字段' },
];

/** 空的输入字段模板 */
const EMPTY_INPUT_FIELD: InputFieldDefinition = {
  key: '',
  label: '',
  type: 'text',
  required: false,
  defaultValue: '',
  placeholder: '',
  options: [],
  description: '',
};

/** 空的提示词模板 */
const EMPTY_PROMPT_CARD: WelcomePromptCard = {
  id: '',
  text: '',
  iconClass: 'fas fa-comment',
};

/** 生成简单唯一 ID */
const generatePromptId = (): string => `prompt_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

interface CapabilityFormState {
  readonly id?: number;
  readonly code: string;
  readonly name: string;
  readonly type: AiDifyCapabilityType;
  readonly api_key: string;
  readonly endpoint: string;
  readonly enabled: boolean;
  readonly features: AiDifyCapabilityFeatures;
  readonly input_fields: InputFieldDefinition[];
  readonly welcome_config: WelcomeConfig;
  readonly placeholder: string;
}

const EMPTY_FORM: CapabilityFormState = {
  code: '',
  name: '',
  type: 'chat',
  api_key: '',
  endpoint: '',
  enabled: true,
  features: { ...DEFAULT_CAPABILITY_FEATURES },
  input_fields: [],
  welcome_config: { ...DEFAULT_WELCOME_CONFIG, prompts: [] },
  placeholder: '',
};

export const DifyCapabilityPage: React.FC = () => {
  const { capabilities, loading, error, createCapability, updateCapability, deleteCapability, toggleEnabled } = useDifyCapability();

  const [showModal, setShowModal] = useState<boolean>(false);
  const [formMode, setFormMode] = useState<FormMode>('create');
  const [formState, setFormState] = useState<CapabilityFormState>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AiDifyCapability | null>(null);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const notificationTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  const modalTitle: string = useMemo((): string => {
    return formMode === 'create' ? '新建 Mind 能力' : '编辑 Mind 能力';
  }, [formMode]);

  const showToast = useCallback((message: string, type: 'success' | 'error' = 'success') => {
    if (notificationTimeoutRef.current) {
      clearTimeout(notificationTimeoutRef.current);
    }
    setNotification({ message, type });
    notificationTimeoutRef.current = setTimeout(() => {
      setNotification(null);
    }, 3000);
  }, []);

  const openCreateModal = useCallback((): void => {
    setFormMode('create');
    setFormState(EMPTY_FORM);
    setSubmitError(null);
    setShowModal(true);
  }, []);

  const openEditModal = useCallback((cap: AiDifyCapability): void => {
    setFormMode('edit');
    setFormState({
      id: cap.id,
      code: cap.code,
      name: cap.name,
      type: cap.type,
      api_key: cap.api_key,
      endpoint: cap.endpoint,
      enabled: cap.enabled,
      features: cap.features
        ? { ...cap.features }
        : { ...DEFAULT_CAPABILITY_FEATURES },
      input_fields: cap.input_fields
        ? cap.input_fields.map(f => ({ ...f }))
        : [],
      welcome_config: cap.welcome_config
        ? {
            title: cap.welcome_config.title,
            subtitle: cap.welcome_config.subtitle,
            prompts: cap.welcome_config.prompts.map(p => ({ ...p })),
          }
        : { ...DEFAULT_WELCOME_CONFIG, prompts: [] },
      placeholder: cap.placeholder ?? '',
    });
    setSubmitError(null);
    setShowModal(true);
  }, []);

  const closeModal = useCallback((): void => {
    if (submitting) {
      return;
    }
    setShowModal(false);
  }, [submitting]);

  const handleInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>): void => {
      const { name, value, type } = event.target;
      if (type === 'checkbox') {
        const checked: boolean = (event.target as HTMLInputElement).checked;
        setFormState((prev: CapabilityFormState): CapabilityFormState => ({
          ...prev,
          [name]: checked
        }));
      } else {
        setFormState((prev: CapabilityFormState): CapabilityFormState => ({
          ...prev,
          [name]: value
        }));
      }
    },
    []
  );

  /** 切换特性开关 */
  const handleFeatureToggle = useCallback(
    (featureKey: keyof AiDifyCapabilityFeatures): void => {
      setFormState((prev: CapabilityFormState): CapabilityFormState => ({
        ...prev,
        features: {
          ...prev.features,
          [featureKey]: !prev.features[featureKey],
        },
      }));
    },
    []
  );

  // ─── 输入字段管理 ─────────────────────

  /** 添加一个输入字段 */
  const handleAddInputField = useCallback((): void => {
    setFormState((prev) => ({
      ...prev,
      input_fields: [...prev.input_fields, { ...EMPTY_INPUT_FIELD }],
    }));
  }, []);

  /** 删除指定索引的输入字段 */
  const handleRemoveInputField = useCallback((index: number): void => {
    setFormState((prev) => ({
      ...prev,
      input_fields: prev.input_fields.filter((_, i) => i !== index),
    }));
  }, []);

  /** 更新指定索引输入字段的某个属性 */
  const handleInputFieldChange = useCallback(
    (index: number, field: keyof InputFieldDefinition, value: unknown): void => {
      setFormState((prev) => ({
        ...prev,
        input_fields: prev.input_fields.map((item, i) =>
          i === index ? { ...item, [field]: value } : item
        ),
      }));
    },
    []
  );

  /** 更新 select 类型字段的 options（逗号分隔） */
  const handleInputFieldOptionsChange = useCallback(
    (index: number, rawValue: string): void => {
      const options = rawValue.split(',').map((s) => s.trim()).filter(Boolean);
      setFormState((prev) => ({
        ...prev,
        input_fields: prev.input_fields.map((item, i) =>
          i === index ? { ...item, options } : item
        ),
      }));
    },
    []
  );

  // ─── 欢迎页配置管理 ─────────────────────

  /** 更新欢迎页标题/副标题 */
  const handleWelcomeConfigChange = useCallback(
    (field: 'title' | 'subtitle', value: string): void => {
      setFormState((prev) => ({
        ...prev,
        welcome_config: {
          ...prev.welcome_config,
          [field]: value,
        },
      }));
    },
    []
  );

  /** 添加一条提示词 */
  const handleAddPrompt = useCallback((): void => {
    setFormState((prev) => ({
      ...prev,
      welcome_config: {
        ...prev.welcome_config,
        prompts: [
          ...prev.welcome_config.prompts,
          { ...EMPTY_PROMPT_CARD, id: generatePromptId() },
        ],
      },
    }));
  }, []);

  /** 删除指定索引的提示词 */
  const handleRemovePrompt = useCallback((index: number): void => {
    setFormState((prev) => ({
      ...prev,
      welcome_config: {
        ...prev.welcome_config,
        prompts: prev.welcome_config.prompts.filter((_, i) => i !== index),
      },
    }));
  }, []);

  /** 更新指定索引提示词的某个属性 */
  const handlePromptChange = useCallback(
    (index: number, field: keyof WelcomePromptCard, value: string): void => {
      setFormState((prev) => ({
        ...prev,
        welcome_config: {
          ...prev.welcome_config,
          prompts: prev.welcome_config.prompts.map((item, i) =>
            i === index ? { ...item, [field]: value } : item
          ),
        },
      }));
    },
    []
  );

  const validateForm = useCallback((state: CapabilityFormState): string | null => {
    if (!state.name.trim()) {
      return '名称不能为空';
    }
    if (!state.endpoint.trim()) {
      return 'AI Base Url 不能为空';
    }
    if (!state.api_key.trim()) {
      return 'API Key 不能为空';
    }
    if (!state.type) {
      return '类型不能为空';
    }
    // 校验输入字段
    for (let i = 0; i < state.input_fields.length; i++) {
      const f = state.input_fields[i];
      if (!f.key.trim()) {
        return `输入字段 #${i + 1} 的"字段名"不能为空`;
      }
      if (!f.label.trim()) {
        return `输入字段 #${i + 1} 的"显示名称"不能为空`;
      }
      if (f.type === 'select' && (!f.options || f.options.length === 0)) {
        return `输入字段 #${i + 1} 类型为"下拉选择"时必须设置选项`;
      }
    }
    // 检查 key 是否重复
    const keys = state.input_fields.map((f) => f.key.trim());
    const dupKey = keys.find((k, i) => keys.indexOf(k) !== i);
    if (dupKey) {
      return `输入字段的"字段名"存在重复: "${dupKey}"`;
    }
    return null;
  }, []);

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>): Promise<void> => {
      event.preventDefault();
      if (submitting) {
        return;
      }

      const validationMsg: string | null = validateForm(formState);
      if (validationMsg) {
        setSubmitError(validationMsg);
        return;
      }

      setSubmitting(true);
      setSubmitError(null);

      try {
        const payload = {
          code: formState.code.trim(),
          name: formState.name.trim(),
          type: formState.type,
          api_key: formState.api_key.trim(),
          endpoint: formState.endpoint.trim(),
          enabled: formState.enabled,
          features: { ...formState.features },
          input_fields: formState.input_fields.map((f) => ({
            key: f.key.trim(),
            label: f.label.trim(),
            type: f.type,
            required: f.required,
            defaultValue: f.defaultValue?.trim() ?? '',
            placeholder: f.placeholder?.trim() ?? '',
            options: f.type === 'select' ? (f.options ?? []) : [],
            description: f.description?.trim() ?? '',
          })),
          welcome_config: {
            title: formState.welcome_config.title.trim(),
            subtitle: formState.welcome_config.subtitle.trim(),
            prompts: formState.welcome_config.prompts.map((p) => ({
              id: p.id || generatePromptId(),
              text: p.text.trim(),
              iconClass: p.iconClass.trim() || 'fas fa-comment',
            })),
          },
          placeholder: formState.placeholder.trim(),
        };

        if (formMode === 'create') {
          await createCapability(payload as AiDifyCapabilityCreatePayload);
        } else if (formMode === 'edit' && typeof formState.id === 'number') {
          await updateCapability(formState.id, payload);
        }
        setShowModal(false);
      } catch (submitErr) {
        console.error('DifyCapabilityPage 提交失败:', submitErr);
        setSubmitError((submitErr as Error)?.message ?? '保存失败');
      } finally {
        setSubmitting(false);
      }
    },
    [createCapability, formMode, formState, submitting, updateCapability, validateForm]
  );

  const handleDelete = useCallback((cap: AiDifyCapability): void => {
    setDeleteTarget(cap);
  }, []);

  const closeDeleteModal = useCallback((): void => {
    if (isDeleting) return;
    setDeleteTarget(null);
  }, [isDeleting]);

  const executeDelete = useCallback(async (): Promise<void> => {
    if (!deleteTarget) return;
    
    setIsDeleting(true);
    try {
      await deleteCapability(deleteTarget.id);
      setDeleteTarget(null);
    } catch (deleteErr) {
      console.error('删除 Mind 能力失败:', deleteErr);
      window.alert('删除失败，请稍后重试');
    } finally {
      setIsDeleting(false);
    }
  }, [deleteCapability, deleteTarget]);

  const handleToggleEnabled = useCallback(
    async (cap: AiDifyCapability): Promise<void> => {
      try {
        const nextState = !cap.enabled;
        await toggleEnabled(cap.id, nextState);
        showToast(`已${nextState ? '启用' : '禁用'} "${cap.name}"`, 'success');
      } catch (toggleErr) {
        console.error('切换启用状态失败:', toggleErr);
        showToast('切换状态失败，请稍后重试', 'error');
      }
    },
    [toggleEnabled, showToast]
  );

  return (
    <div className="dify-page">
      <div className="dify-page-card">
        <div className="dify-page-header">
            <div>
            <h2 className="dify-page-title">Mind 能力配置</h2>
            <p className="dify-page-subtitle">配置 chat / workflow / agent 的 Base Url 与 API Key，用于业务侧 Copilot 调用</p>
            </div>
            <button className="dify-btn-primary" type="button" onClick={openCreateModal}>
            新建能力
            </button>
        </div>

        {capabilities.length > 0 && (
            <div className="dify-page-table-wrapper">
              <div className="dify-card-grid">
                {capabilities.map((cap: AiDifyCapability): React.ReactNode => (
                  <div className="dify-card-item" key={cap.id}>
                    <div className="dify-card-header-row">
                      <h3 className="dify-card-title">{cap.name}</h3>
                      <span className={`dify-card-badge type-${cap.type}`}>
                        {cap.type}
                      </span>
                    </div>

                    {cap.features && (
                      <div className="dify-feature-tags">
                        {FEATURE_TOGGLE_LIST.map((ft) => (
                          cap.features?.[ft.key] ? (
                            <span className="dify-feature-tag active" key={ft.key}>{ft.label}</span>
                          ) : null
                        ))}
                      </div>
                    )}

                    {/* 输入字段标签展示 */}
                    {/* {cap.input_fields && cap.input_fields.length > 0 && (
                      <div className="dify-feature-tags">
                        {cap.input_fields.map((f) => (
                          <span
                            className={`dify-feature-tag ${f.required ? 'required' : ''}`}
                            key={f.key}
                            title={f.description || `${f.label} (${f.type})`}
                          >
                            {f.label}{f.required ? ' *' : ''}
                          </span>
                        ))}
                      </div>
                    )} */}

                     {/* 欢迎页配置预览 */}
                    {cap.welcome_config && (cap.welcome_config.title || cap.welcome_config.prompts.length > 0) && (
                      <div className="dify-welcome-preview">
                        {cap.welcome_config.title && (
                          <div className="dify-welcome-preview-title">{cap.welcome_config.title}</div>
                        )}
                        {cap.welcome_config.prompts.length > 0 && (
                          <div className="dify-welcome-preview-prompts">
                            {cap.welcome_config.prompts.map((p) => (
                              <span className="dify-welcome-preview-prompt-tag" key={p.id}>
                                <i className={p.iconClass} style={{ marginRight: 4, fontSize: 10 }} />
                                {p.text.length > 20 ? `${p.text.slice(0, 20)}...` : p.text}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    <div className="dify-card-body-row">
                      <div className="dify-info-group">
                        <span className="dify-info-label">Code</span>
                        <div className="dify-info-value">{cap.code ? cap.code : '未设置'}</div>
                      </div>
                      <div className="dify-info-group">
                        <span className="dify-info-label">AI Base Url</span>
                        <div className="dify-info-value">{cap.endpoint}</div>
                      </div>
                      
                      <div className="dify-info-date">
                        创建于 {cap.date_created ? cap.date_created.split('T')[0] : '-'}
                      </div>
                    </div>

                    <div className="dify-card-footer-row">
                      <label className="dify-switch" title={cap.enabled ? '已启用' : '已禁用'}>
                        <input
                          type="checkbox"
                          checked={cap.enabled}
                          onChange={(): Promise<void> => handleToggleEnabled(cap)}
                        />
                        <span className="dify-switch-slider" />
                      </label>

                      <div className="dify-card-actions">
                        <button
                          type="button"
                          className="dify-action-btn edit"
                          onClick={(): void => openEditModal(cap)}
                        >
                          编辑
                        </button>
                        <button
                          type="button"
                          className="dify-action-btn delete"
                          onClick={(): void => handleDelete(cap)}
                        >
                          删除
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
        )}

        {showModal && (
             <div className="dify-modal-mask">
                <div className="dify-modal">
                    <div className="dify-modal-header">
                        <h3 className="dify-modal-title">{modalTitle}</h3>
                        <button className="dify-modal-close" type="button" onClick={closeModal}>×</button>
                    </div>
                    <form className="dify-modal-wrapper-form" onSubmit={handleSubmit}>
                        <div className="dify-modal-body">
                          <div className="dify-form-item">
                            <label className="dify-form-label" htmlFor="name">名称</label>
                            <input id="name" name="name" className="dify-form-input" type="text" value={formState.name} onChange={handleInputChange} />
                          </div>
                          <div className="dify-form-item"> 
                            <label className="dify-form-label" htmlFor="code">编码</label>
                            <input id="code" name="code" className="dify-form-input" type="text" value={formState.code} onChange={handleInputChange} />
                          </div>
                          <div className="dify-form-item">
                            <label className="dify-form-label" htmlFor="type">类型</label>
                            <select id="type" name="type" className="dify-form-select" value={formState.type} onChange={handleInputChange}>
                                <option value="chat">chat</option>
                                <option value="workflow">workflow</option>
                                <option value="agent">agent</option>
                            </select>
                          </div>
                          <div className="dify-form-item">
                            <label className="dify-form-label" htmlFor="endpoint">AI Base Url</label>
                            <input id="endpoint" name="endpoint" className="dify-form-input" type="text" value={formState.endpoint} onChange={handleInputChange} />
                          </div>
                          <div className="dify-form-item">
                            <label className="dify-form-label" htmlFor="api_key">API Key</label>
                            <input id="api_key" name="api_key" className="dify-form-input" type="password" value={formState.api_key} onChange={handleInputChange} />
                          </div>
                          <div className="dify-form-item dify-form-item-inline">
                            <input id="enabled" name="enabled" type="checkbox" checked={formState.enabled} onChange={handleInputChange} style={{marginRight:8}}/>
                            <label className="dify-form-label" htmlFor="enabled" style={{marginBottom:0}}>启用</label>
                          </div>

                          {/* ====== 特性开关区域 ====== */}
                          <div className="dify-features-section">
                            <div className="dify-features-section-title">功能开关</div>
                            <div className="dify-features-grid">
                              {FEATURE_TOGGLE_LIST.map((ft: FeatureToggleItem) => (
                                <div
                                  className={`dify-feature-card ${formState.features[ft.key] ? 'active' : ''}`}
                                  key={ft.key}
                                  onClick={(): void => handleFeatureToggle(ft.key)}
                                  role="button"
                                  tabIndex={0}
                                  onKeyDown={(e): void => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      handleFeatureToggle(ft.key);
                                    }
                                  }}
                                >
                                  <div className="dify-feature-card-header">
                                    <span className="dify-feature-card-label">{ft.label}</span>
                                    <label className="dify-switch dify-switch-sm" onClick={(e): void => e.stopPropagation()}>
                                      <input
                                        type="checkbox"
                                        checked={formState.features[ft.key]}
                                        onChange={(): void => handleFeatureToggle(ft.key)}
                                      />
                                      <span className="dify-switch-slider" />
                                    </label>
                                  </div>
                                  <div className="dify-feature-card-desc">{ft.description}</div>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* ====== 欢迎页配置区域 ====== */}
                          <div className="dify-features-section">
                            <div className="dify-features-section-title">欢迎页配置</div>

                            <div className="dify-form-item">
                              <label className="dify-form-label">大标题</label>
                              <input
                                className="dify-form-input"
                                type="text"
                                value={formState.welcome_config.title}
                                placeholder="如：电气元件选型设计"
                                onChange={(e): void => handleWelcomeConfigChange('title', e.target.value)}
                              />
                            </div>

                            <div className="dify-form-item">
                              <label className="dify-form-label">副标题</label>
                              <input
                                className="dify-form-input"
                                type="text"
                                value={formState.welcome_config.subtitle}
                                placeholder="如：使用以下提示词开始，或直接输入您的问题"
                                onChange={(e): void => handleWelcomeConfigChange('subtitle', e.target.value)}
                              />
                            </div>

                            <div className="dify-form-item">
                              <label className="dify-form-label">输入框占位提示</label>
                              <input
                                className="dify-form-input"
                                name="placeholder"
                                type="text"
                                value={formState.placeholder}
                                placeholder="如：随便问点什么...（留空则使用默认文案）"
                                onChange={handleInputChange}
                              />
                            </div>

                            <div className="dify-welcome-prompts-header">
                              <span className="dify-form-label" style={{ marginBottom: 0 }}>提示词卡片</span>
                              <button
                                type="button"
                                className="dify-btn-primary"
                                style={{ fontSize: 12, padding: '4px 12px' }}
                                onClick={handleAddPrompt}
                              >
                                + 添加提示词
                              </button>
                            </div>

                            {formState.welcome_config.prompts.length === 0 && (
                              <div className="dify-welcome-empty-hint">
                                暂无提示词，点击"添加提示词"开始配置
                              </div>
                            )}

                            {formState.welcome_config.prompts.map((prompt, index) => (
                              <div className="dify-welcome-prompt-card" key={prompt.id || index}>
                                <div className="dify-welcome-prompt-card-header">
                                  <span className="dify-welcome-prompt-card-index">提示词 #{index + 1}</span>
                                  <button
                                    type="button"
                                    className="dify-action-btn delete"
                                    style={{ fontSize: 12, padding: '2px 8px' }}
                                    onClick={(): void => handleRemovePrompt(index)}
                                  >
                                    删除
                                  </button>
                                </div>

                                <div className="dify-welcome-prompt-card-body">
                                  <div className="dify-form-item" style={{ flex: 1 }}>
                                    <label className="dify-form-label">提示词内容 *</label>
                                    <input
                                      className="dify-form-input"
                                      type="text"
                                      value={prompt.text}
                                      placeholder="如：为西门子 S7-1200 编写一段电机启停的 SCL 程序"
                                      onChange={(e): void => handlePromptChange(index, 'text', e.target.value)}
                                    />
                                  </div>

                                  <div className="dify-form-item" style={{ width: 180 }}>
                                    <label className="dify-form-label">图标类名</label>
                                    <input
                                      className="dify-form-input"
                                      type="text"
                                      value={prompt.iconClass}
                                      placeholder="fas fa-code"
                                      onChange={(e): void => handlePromptChange(index, 'iconClass', e.target.value)}
                                    />
                                  </div>
                                </div>

                                {/* 实时预览 */}
                                {prompt.text.trim() && (
                                  <div className="dify-welcome-prompt-preview">
                                    <i className={prompt.iconClass || 'fas fa-comment'} style={{ marginRight: 6 }} />
                                    <span>{prompt.text}</span>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>

                          {/* ====== 输入字段配置区域 ====== */}
                          {/* <div className="dify-features-section">
                            <div className="dify-features-section-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span>输入字段配置</span>
                              <button
                                type="button"
                                className="dify-btn-primary"
                                style={{ fontSize: 12, padding: '4px 12px' }}
                                onClick={handleAddInputField}
                              >
                                + 添加字段
                              </button>
                            </div>

                            {formState.input_fields.length === 0 && (
                              <div style={{ color: '#999', fontSize: 13, padding: '12px 0' }}>
                                暂无自定义输入字段，点击"添加字段"开始配置
                              </div>
                            )}

                            {formState.input_fields.map((field, index) => (
                              <div className="dify-input-field-card" key={index}>
                                <div className="dify-input-field-card-header">
                                  <span className="dify-input-field-card-index">字段 #{index + 1}</span>
                                  <button
                                    type="button"
                                    className="dify-action-btn delete"
                                    style={{ fontSize: 12, padding: '2px 8px' }}
                                    onClick={(): void => handleRemoveInputField(index)}
                                  >
                                    删除
                                  </button>
                                </div>

                                <div className="dify-input-field-grid">
                                  <div className="dify-form-item">
                                    <label className="dify-form-label">字段名 (key) *</label>
                                    <input
                                      className="dify-form-input"
                                      type="text"
                                      value={field.key}
                                      placeholder="如 table_id"
                                      onChange={(e): void => handleInputFieldChange(index, 'key', e.target.value)}
                                    />
                                  </div>

                                  <div className="dify-form-item">
                                    <label className="dify-form-label">显示名称 *</label>
                                    <input
                                      className="dify-form-input"
                                      type="text"
                                      value={field.label}
                                      placeholder="如 表格ID"
                                      onChange={(e): void => handleInputFieldChange(index, 'label', e.target.value)}
                                    />
                                  </div>

                                  <div className="dify-form-item">
                                    <label className="dify-form-label">字段类型</label>
                                    <select
                                      className="dify-form-select"
                                      value={field.type}
                                      onChange={(e): void => handleInputFieldChange(index, 'type', e.target.value)}
                                    >
                                      {INPUT_FIELD_TYPE_OPTIONS.map((opt) => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                      ))}
                                    </select>
                                  </div>

                                  <div className="dify-form-item dify-form-item-inline" style={{ alignItems: 'center', paddingTop: 22 }}>
                                    <input
                                      type="checkbox"
                                      checked={field.required}
                                      onChange={(e): void => handleInputFieldChange(index, 'required', e.target.checked)}
                                      style={{ marginRight: 8 }}
                                    />
                                    <label className="dify-form-label" style={{ marginBottom: 0 }}>必填</label>
                                  </div>

                                  <div className="dify-form-item">
                                    <label className="dify-form-label">默认值</label>
                                    <input
                                      className="dify-form-input"
                                      type="text"
                                      value={field.defaultValue ?? ''}
                                      placeholder="可选"
                                      onChange={(e): void => handleInputFieldChange(index, 'defaultValue', e.target.value)}
                                    />
                                  </div>

                                  <div className="dify-form-item">
                                    <label className="dify-form-label">占位提示</label>
                                    <input
                                      className="dify-form-input"
                                      type="text"
                                      value={field.placeholder ?? ''}
                                      placeholder="可选"
                                      onChange={(e): void => handleInputFieldChange(index, 'placeholder', e.target.value)}
                                    />
                                  </div>

                                  {field.type === 'select' && (
                                    <div className="dify-form-item" style={{ gridColumn: '1 / -1' }}>
                                      <label className="dify-form-label">选项 (逗号分隔)</label>
                                      <input
                                        className="dify-form-input"
                                        type="text"
                                        value={(field.options ?? []).join(', ')}
                                        placeholder="选项A, 选项B, 选项C"
                                        onChange={(e): void => handleInputFieldOptionsChange(index, e.target.value)}
                                      />
                                    </div>
                                  )}

                                  <div className="dify-form-item" style={{ gridColumn: '1 / -1' }}>
                                    <label className="dify-form-label">描述</label>
                                    <input
                                      className="dify-form-input"
                                      type="text"
                                      value={field.description ?? ''}
                                      placeholder="字段用途说明（可选）"
                                      onChange={(e): void => handleInputFieldChange(index, 'description', e.target.value)}
                                    />
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div> */}

                          {submitError && <div className="dify-form-error">{submitError}</div>}
                        </div>
                        <div className="dify-modal-footer">
                          <button type="button" className="dify-btn-secondary" onClick={closeModal} disabled={submitting}>取消</button>
                          <button type="submit" className="dify-btn-primary" disabled={submitting}>{submitting ? '提交中...' : '确定'}</button>
                        </div>
                    </form>
                </div>
             </div>
        )}

        {deleteTarget && (
            <div className="dify-modal-mask">
                <div className="dify-modal" style={{ width: 420 }}>
                    <div className="dify-modal-header">
                        <h3 className="dify-modal-title">确认删除</h3>
                        <button className="dify-modal-close" type="button" onClick={closeDeleteModal}>×</button>
                    </div>
                    <div className="dify-modal-body dify-modal-danger-content">
                        <h4 className="dify-modal-danger-title">
                            ⚠️ 将要删除「{deleteTarget.name}」
                        </h4>
                        <p className="dify-modal-danger-text">
                            删除后，所有使用此配置的助手将无法正常工作。此操作无法撤销。
                        </p>
                    </div>
                    <div className="dify-modal-footer">
                        <button type="button" className="dify-btn-secondary" onClick={closeDeleteModal} disabled={isDeleting}>
                            取消
                        </button>
                        <button type="button" className="dify-btn-danger-primary" onClick={executeDelete} disabled={isDeleting}>
                            {isDeleting ? '删除中...' : '确认删除'}
                        </button>
                    </div>
                </div>
            </div>
        )}

        {notification && (
            <div className={`dify-toast ${notification.type}`}>
                <div className="dify-toast-icon">
                    {notification.type === 'success' ? <i className="fa-solid fa-circle-check"></i> : <i className="fa-solid fa-circle-xmark"></i>}
                </div>
                <div className="dify-toast-content">{notification.message}</div>
            </div>
        )}
      </div>
    </div>
  );
};